import { useEffect, useState } from 'react'
import { useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useDebounce } from 'use-debounce'
import {
  Button,
  ButtonBlock,
  DateField,
  FieldBlock,
  Font,
  Grid,
  List,
  Modal,
  ProgressBar,
  Section,
  SelectField,
  TextBlock,
  TextField,
  useToast,
} from '@politechdev/blocks-design-system'
import { useRequest } from 'hooks'
import {
  AssociatedFlag,
  FlagFilters,
  PerformanceReview,
  UpdateReviewRequestResult,
  SubmittablePerformanceReview,
} from 'performanceReviews/types'
import {
  getIdsForFlagsUpdate,
  navigateToFlag,
  searchFlags,
} from 'performanceReviews/utils'
import { ClearableOptions } from 'types/utils'
import FlagListItem from './FlagListItem'

type DateType = Parameters<typeof DateField>[0]['type']

const TYPE_OPTIONS: ClearableOptions<string> = [
  { label: 'Canvasser', value: 'canvasser' },
  { label: 'Packet', value: 'shift' },
]

const AddFlagsModal = ({
  isOpen,
  setIsOpen,
  performanceReview,
  updateReview,
}: {
  isOpen: boolean
  setIsOpen: (value: boolean) => void
  performanceReview: PerformanceReview
  updateReview: (
    fields: SubmittablePerformanceReview
  ) => UpdateReviewRequestResult
}) => {
  const { t } = useTranslation()
  const { setToast } = useToast()
  const { id: idString } = useParams<{ id: string }>()

  const [isUpdateLoading, setIsUpdateLoading] = useState(false)
  const [filters, setFilters] = useState<FlagFilters>({})
  const [selectedFlags, setSelectedFlags] = useState<AssociatedFlag[]>([])

  const [debouncedFilters] = useDebounce(filters, 250)

  const updateFilter = (update: Partial<FlagFilters>) =>
    setFilters(current => ({ ...current, ...update }))

  const searchFlagsRequest = useRequest(searchFlags, {
    onError: () => {
      setToast({
        message: t('There was an issue gettings flags'),
        variant: 'error',
      })
    },
  })

  useEffect(() => {
    if (!isOpen) {
      setFilters({})
      setSelectedFlags([])
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen && idString) {
      void searchFlagsRequest.makeRequest(+idString, debouncedFilters)
    }
  }, [idString, isOpen, debouncedFilters])

  const toggleFlag = (isSelected: boolean, flag: AssociatedFlag) => {
    if (!isSelected) {
      setSelectedFlags(currentFlags => currentFlags.concat(flag))
    } else {
      setSelectedFlags(currentFlags =>
        currentFlags.filter(selected => selected.id !== flag.id)
      )
    }
  }

  const handleUpdate = async () => {
    const { flagIds, shiftIds } = getIdsForFlagsUpdate(
      performanceReview,
      selectedFlags
    )

    setIsUpdateLoading(true)
    const response = await updateReview({
      flag_ids: flagIds,
      shift_ids: shiftIds,
    })
    setIsUpdateLoading(false)

    if (response?.performance_review) {
      setIsOpen(false)
    }
  }

  const onContinue = () => {
    void handleUpdate()
  }

  const onCancel = () => {
    setIsOpen(false)
  }

  return (
    <Modal title={t('Add flag')} isOpen={isOpen}>
      <Modal.Body>
        <FieldBlock>
          <DateField
            label={t('Date range')}
            type={'range' as DateType}
            customPresets={[]}
            showPresets
            startDate={filters.start_date}
            endDate={filters.end_date}
            onChange={(start_date, end_date) =>
              updateFilter({ start_date, end_date })
            }
            onClear={() =>
              updateFilter({ start_date: undefined, end_date: undefined })
            }
            clearable
          />
          <TextField
            label={t('Flag id')}
            type="search"
            value={filters.flagId}
            onChange={flagId => updateFilter({ flagId: flagId as string })}
          />
          <SelectField
            label={t('Flag type')}
            value={filters.type}
            options={TYPE_OPTIONS}
            onSelect={type => updateFilter({ type })}
            clearable
          />
        </FieldBlock>
        <Section>
          <ProgressBar show={isUpdateLoading} />
          <List
            loading={searchFlagsRequest.isLoading}
            emptyMessage={t('No matching flags found')}
            itemData={
              searchFlagsRequest.response?.['quality_control/flags'] ?? []
            }
            render={item => {
              const flag = item as AssociatedFlag
              const isSelected = selectedFlags.some(
                selected => selected.id === flag.id
              )
              return (
                <FlagListItem flag={flag}>
                  <Button.Secondary onClick={() => navigateToFlag(flag)}>
                    {t('View')}
                  </Button.Secondary>
                  {isSelected ? (
                    <Button.Secondary
                      onClick={() => toggleFlag(isSelected, flag)}
                    >
                      {t('Deselect')}
                    </Button.Secondary>
                  ) : (
                    <Button onClick={() => toggleFlag(isSelected, flag)}>
                      {t('Select')}
                    </Button>
                  )}
                </FlagListItem>
              )
            }}
          />
        </Section>
      </Modal.Body>
      <Modal.Actions>
        <Grid>
          <TextBlock>
            <Font.Copy variant="hint">
              {t('{{ count }} flags selected', { count: selectedFlags.length })}
            </Font.Copy>
          </TextBlock>
          <ButtonBlock justify="right">
            <Button.Secondary onClick={onCancel}>
              {t('Cancel')}
            </Button.Secondary>
            <Button.Accent onClick={onContinue}>{t('Submit')}</Button.Accent>
          </ButtonBlock>
        </Grid>
      </Modal.Actions>
    </Modal>
  )
}

export default AddFlagsModal
